// Pure utility functions for Compliance Master: financial year options,
// compliance frequency derivation, Entity Name / Entity TAN selection sync,
// and Compliance Period display formatting.

const FLOOR_FY = '2025-26'
const FLOOR_FY_START_YEAR = 2025

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

// Parses a 'DD/MM/YYYY' string into a Date. Returns null if malformed.
const parseDDMMYYYY = dateStr => {
  if (!dateStr || typeof dateStr !== 'string') return null
  const parts = dateStr.split('/')
  if (parts.length !== 3) return null
  const [day, month, year] = parts.map(Number)
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

// Start Date date-picker lower bound: April 1, 2025 (start of FY 2025-26).
export const MIN_COMPLIANCE_PERIOD_DATE = new Date(FLOOR_FY_START_YEAR, 3, 1)

/**
 * Returns the financial year string (e.g. "2025-26") that contains the given date.
 * Indian FY runs April 1 - March 31.
 */
export const getFinancialYearForDate = date => {
  const year = date.getFullYear()
  const month = date.getMonth() // 0-indexed, 0 = January, 3 = April
  const startYear = month >= 3 ? year : year - 1
  const endYearSuffix = String((startYear + 1) % 100).padStart(2, '0')
  return `${startYear}-${endYearSuffix}`
}

/**
 * Generates the list of financial year options, starting at the FY containing
 * `referenceDate`, descending one FY at a time, down to (and including) "2023-24".
 * If referenceDate's FY is already at or before 2023-24, returns just ["2023-24"].
 */
export const getFinancialYearOptions = (referenceDate = new Date()) => {
  const currentFY = getFinancialYearForDate(referenceDate)
  const currentStartYear = parseInt(currentFY.split('-')[0], 10)

  if (currentStartYear <= FLOOR_FY_START_YEAR) {
    return [FLOOR_FY]
  }

  const options = []
  for (let startYear = currentStartYear; startYear >= FLOOR_FY_START_YEAR; startYear--) {
    const endYearSuffix = String((startYear + 1) % 100).padStart(2, '0')
    options.push(`${startYear}-${endYearSuffix}`)
  }
  return options
}

/**
 * Returns the de-duplicated, stably-ordered list of frequencies for the selected
 * category names, based on the provided category list ({ name, frequency }).
 */
export const deriveComplianceFrequency = (selectedCategoryNames = [], categoryList = []) => {
  const frequencies = []
  selectedCategoryNames.forEach(categoryName => {
    const category = categoryList.find(cat => cat.name === categoryName)
    if (category && !frequencies.includes(category.frequency)) {
      frequencies.push(category.frequency)
    }
  })
  return frequencies
}

/**
 * Formats a Compliance Period for display based on its Compliance Frequency.
 *
 * - Monthly: "Apr-2026" (the picked date's month and year)
 * - Quarterly: "Apr to Jun26" (the quarter containing the picked date's month,
 *   using the standard Jan-Mar / Apr-Jun / Jul-Sep / Oct-Dec calendar-quarter
 *   grouping)
 * - Annual (or any other/unrecognized frequency): the financial year string
 *   itself (e.g. "2025-26"), unchanged
 *
 * @param {string} compliancePeriod - the stored FY string, e.g. "2025-26"
 * @param {string} compliancePeriodDate - the picked date as 'DD/MM/YYYY', used
 *   to resolve which month/quarter within the FY to display
 * @param {string[]} complianceFrequency - the derived frequency list (e.g. ['Monthly'])
 * @returns {string} the formatted Compliance Period label
 */
export const formatCompliancePeriod = (
  compliancePeriod,
  compliancePeriodDate,
  complianceFrequency = []
) => {
  if (!compliancePeriod) return ''

  const frequency = complianceFrequency[0]
  const date = parseDDMMYYYY(compliancePeriodDate)

  if (!date || (frequency !== 'Monthly' && frequency !== 'Quarterly')) {
    return compliancePeriod
  }

  const month = date.getMonth() // 0-indexed
  const year = date.getFullYear()

  if (frequency === 'Monthly') {
    return `${MONTH_ABBREVIATIONS[month]}-${year}`
  }

  // Quarterly: group into Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec (calendar-year aligned)
  const quarterStartMonth = Math.floor(month / 3) * 3
  const quarterEndMonth = quarterStartMonth + 2
  const startLabel = MONTH_ABBREVIATIONS[quarterStartMonth]
  const endLabel = MONTH_ABBREVIATIONS[quarterEndMonth]
  const yearSuffix = String(year % 100).padStart(2, '0')

  return `${startLabel} to ${endLabel}${yearSuffix}`
}

/**
 * Keeps Entity Name and Entity TAN selections bidirectionally consistent and
 * auto-populated from whichever field the admin last changed.
 *
 * @param {Object} params
 * @param {string[]} params.entityNames - currently selected entity names
 * @param {string[]} params.entityTANs - currently selected entity TANs
 * @param {'entityNames'|'entityTANs'} params.changedField - which field just changed
 * @param {Array<{entityName: string, tan: string}>} params.tanLookup - full entity/TAN lookup data
 * @returns {{entityNames: string[], entityTANs: string[]}}
 */
export const syncEntityNamesAndTans = ({
  entityNames = [],
  entityTANs = [],
  changedField,
  tanLookup = [],
}) => {
  if (changedField === 'entityNames') {
    // Auto-select every TAN owned by the currently selected entity names
    const updatedTANs = [
      ...new Set(
        tanLookup
          .filter(item => entityNames.includes(item.entityName))
          .map(item => item.tan)
      ),
    ]
    return { entityNames, entityTANs: updatedTANs }
  }

  if (changedField === 'entityTANs') {
    // Keep entity names unchanged when TANs are modified — the user explicitly
    // selected those entities and unchecking TANs should not clear them.
    return { entityNames, entityTANs }
  }

  return { entityNames, entityTANs }
}
